// per docs/shape-uc-30-sites.md §1, §3 — the two reads UC30 needs, shared by the
// list and the editor so they cannot drift on what a site or a locality is.
import { apiClient } from '../../app/api-client';

export interface SiteRow {
  id: string;
  registrarId: string;
  registrarName: string;
  internalCode: string;
  name: string;
  tip: string;
  adresa: string;
  geographyId: string;
  mpassOrgCode: string;
  // CF-48: `active` is "neither block set" (docs/shape-ops-block-levels.md).
  active: boolean;
  blockedBySystemAdmin: boolean;
  blockedByRegistrar: boolean;
  updatedAtIso: string;
}

// The types the catalogue accepts, in its order (catalog-service SITE_TIP_VALUES).
// docs/shape-uc-30-sites.md §9
export const SITE_TIPS = ['farmacie', 'depozit', 'imsp', 'cabinet', 'altele'] as const;

export interface LocalityDto {
  id: string;
  // '' for a root of the tree.
  parentId: string;
  name: string;
  kind: string;
  area: string;
  active: boolean;
}

// UC30-05 chooses a locality, and the CUATM tree also carries the levels that
// group localities rather than being one. Stated as what to exclude, not what to
// allow: the kind list grows with the nomenclator, and a kind this build has not
// heard of must still be pickable rather than silently blocking a real site.
const GROUPING_KINDS = new Set(['COUNTRY', 'REGION', 'RAION']);

export async function fetchSites(registrarId: string): Promise<SiteRow[]> {
  const { data } = await apiClient.get<{ sites: SiteRow[] }>('/admin/sites', {
    params: registrarId ? { registrarId } : {},
  });
  return data.sites ?? [];
}

// UC30-02, answered by the server: `q` matches the code or the name, ignoring
// case and diacritics; `geographyId` the node and every node under it; `active`
// the effective state ('' is any). docs/shape-uc-30-sites.md §7
export interface SiteCriteria {
  q: string;
  geographyId: string;
  active: '' | 'true' | 'false';
}

// `sites` meets the criteria; the scope facts are taken before them.
export interface SiteRegister {
  sites: SiteRow[];
  scopeTotal: number;
  scopeGeographyIds: string[];
}

export async function fetchSiteRegister(
  registrarId: string,
  criteria: SiteCriteria,
): Promise<SiteRegister> {
  const params = {
    ...(registrarId ? { registrarId } : {}),
    ...(criteria.q ? { q: criteria.q } : {}),
    ...(criteria.geographyId ? { geographyId: criteria.geographyId } : {}),
    ...(criteria.active ? { active: criteria.active } : {}),
  };
  const { data } = await apiClient.get<Partial<SiteRegister>>('/admin/sites', { params });
  const sites = data.sites ?? [];
  return {
    sites,
    scopeTotal: data.scopeTotal ?? sites.length,
    scopeGeographyIds: data.scopeGeographyIds ?? [],
  };
}

// The areas the Localitate filter offers: every node a site of the scope sits
// on, and each node above it, so a raion or a municipality can be chosen
// (TC-UC30-02-01). The country, if the tree has one, would narrow nothing.
export function filterAreas(
  geographyIds: readonly string[],
  localityById: ReadonlyMap<string, LocalityDto>,
): LocalityDto[] {
  const seen = new Set<string>();
  const areas: LocalityDto[] = [];
  for (const id of geographyIds) {
    let node = localityById.get(id);
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      if (node.kind !== 'COUNTRY') areas.push(node);
      node = node.parentId ? localityById.get(node.parentId) : undefined;
    }
  }
  return areas;
}

export async function fetchLocalities(): Promise<LocalityDto[]> {
  const { data } = await apiClient.get<{ localities: LocalityDto[] }>('/admin/sites/localities');
  return data.localities ?? [];
}

// UC30-05: the nodes the catalogue accepts as a site's locality. That is an
// active node that groups nothing: not a grouping kind, and no active node under
// it. `keepId` is the node an edited site already sits on, which stays among the
// options so the field still names it. docs/shape-uc-30-sites.md §8
export function pickableLocalities(localities: LocalityDto[], keepId = ''): LocalityDto[] {
  const subdivided = new Set(
    localities.filter((locality) => locality.active && locality.parentId).map((l) => l.parentId),
  );
  return localities.filter(
    (locality) =>
      locality.id === keepId ||
      (locality.active && !GROUPING_KINDS.has(locality.kind) && !subdivided.has(locality.id)),
  );
}

// 184 CUATM localities share a name with another one; the parent is what
// separates them on screen.
export function localityLabel(locality: LocalityDto): string {
  return locality.area ? `${locality.name} (${locality.area})` : locality.name;
}
